import React, { useEffect } from 'react';
import Button from './Button';
import s from './ConfirmDialog.module.css';

// A centred confirm, for the one thing a drawer cannot ask about itself:
// whether to keep the edits you are about to throw away.
//
// MMS guards every drawer dismissal this way — closing, backing out, or
// clicking the scrim on a form with unsaved input opens "Unsaved Changes" with
// three ways out. Three, not two, matters: "Cancel" returns you to the form,
// which is what you want when the click was an accident, and it is a different
// answer from "Don't Save".
//
// Deliberately not `window.confirm`: a native dialog blocks the event loop and
// cannot carry a third option.
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Save',
  discardLabel = "Don't Save",
  cancelLabel = 'Cancel',
  onConfirm = null,
  confirmDisabled = false,
  onDiscard,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  // No dismiss-on-scrim here: this dialog exists because a stray click was about
  // to lose work, so a stray click must not resolve it either.
  return (
    <div className={s.scrim}>
      <div className={s.dialog} role="alertdialog" aria-modal="true" aria-label={title}>
        <div className={s.title}>{title}</div>
        <div className={s.message}>{message}</div>
        <div className={s.foot}>
          <Button variant="secondary" onClick={onDiscard}>{discardLabel}</Button>
          <span className={s.spacer} />
          {/* Two-button use (a plain confirm) passes no cancel label: the
              discard slot is already the "no" answer, and an unlabelled
              button between them reads as a rendering bug. */}
          {cancelLabel ? (
            <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          ) : null}
          {onConfirm ? (
            <Button onClick={onConfirm} disabled={confirmDisabled}>{confirmLabel}</Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
